import React, { createElement, useState, useCallback, useRef, useEffect } from "react"; // React is need for react-select
import ReactSelect from "react-select";
import Async from "react-select/async";
import { getJSON } from "../utils/request";
import i18n from "../utils/i18n";

// Carried over from `Widgets.Chosen`'s ajax branch, which is what these fields used before: it
// overrode ajax-chosen's own defaults with a two-character threshold, and ajax-chosen waited half a
// second after the last keystroke before asking the server. Without the wait, react-select would
// fire a request per keystroke.
const MIN_TERM_LENGTH = 2;
const TYPE_DELAY = 500;

// react-select ships its own emotion theme, which does not look like the Bootstrap 3 inputs it sits
// next to on the event settings and organizer forms.
//
// The values are not repeated here: they are CSS custom properties published by
// `app/assets/stylesheets/_theme_tokens.scss`, which derives them from the theme's own Sass
// variables. That partial is the single source, and it stays the single source when these
// stylesheets move from Sprockets to shakapacker. There are deliberately no hex fallbacks - a
// missing variable should show up as a failure in spec/system/js_assets/react_select_spec.rb,
// not be papered over by a stale copy of the palette.
const THEME = {
  border: "var(--dtd-form-border)",
  borderFocus: "var(--dtd-form-border-focus)",
  radius: "var(--dtd-form-radius)",
  height: "var(--dtd-form-height)",
  text: "var(--dtd-text)",
  placeholder: "var(--dtd-form-placeholder)",
  optionFocused: "var(--dtd-gray)",
  optionSelected: "var(--dtd-light-gray)",
  removeHover: "var(--dtd-light-gray)", // in place of react-select's red
};

// Only the parts that clashed: the control's border, radius and focus ring; the grey-on-grey
// dropdown highlight; and the chip's remove button, whose default hover is red - which reads as
// destructive next to this app's other multi selects. The chip shape is left as react-select draws
// it.
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: THEME.height,
    backgroundColor: "white",
    borderColor: state.isFocused ? THEME.borderFocus : THEME.border,
    borderRadius: THEME.radius,
    boxShadow: "none",
    "&:hover": {
      borderColor: state.isFocused ? THEME.borderFocus : THEME.border,
    },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: THEME.radius,
    border: `1px solid ${THEME.border}`,
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.08)",
    marginTop: 2,
  }),
  option: (base, state) => {
    let backgroundColor = "transparent";
    if (state.isSelected) {
      backgroundColor = THEME.optionSelected;
    } else if (state.isFocused) {
      backgroundColor = THEME.optionFocused;
    }

    return {
      ...base,
      backgroundColor,
      color: THEME.text,
      // react-select paints a blue flash on mouse-down, independently of the states above.
      "&:active": { backgroundColor: THEME.optionSelected },
    };
  },
  multiValueRemove: (base) => ({
    ...base,
    color: THEME.text,
    "&:hover": { backgroundColor: THEME.removeHover, color: THEME.text },
  }),
  placeholder: (base) => ({ ...base, color: THEME.placeholder }),
  singleValue: (base) => ({ ...base, color: THEME.text }),
  input: (base) => ({ ...base, color: THEME.text }),
};

const normalizeSearchTerm = (term) => term.trim();

const Select = (props) => {
  const isMulti = props.multi === true;
  // react-select's own prop name, because it is passed straight through to it. It is read here as
  // well, for the hidden inputs: `isDisabled` stops the control being interacted with and says
  // nothing about what the form posts.
  const isDisabled = props.isDisabled === true;
  // An empty array is a sensible "nothing selected" for a multi select; react-select wants null.
  const initialValue = props.value || (isMulti ? [] : null);
  const [values, setValues] = useState(initialValue);
  const [searchFailed, setSearchFailed] = useState(false);
  const pendingRequest = useRef(null);
  const pendingKeystroke = useRef(null);
  const pendingResolve = useRef(null);
  const container = useRef(null);
  // What to go back to on a form reset. Held in a ref so the listener below does not have to be
  // rebound whenever the rendered value changes.
  const resetValue = useRef(initialValue);

  useEffect(
    () => () => {
      clearTimeout(pendingKeystroke.current);
      if (pendingResolve.current) pendingResolve.current([]);
      if (pendingRequest.current) pendingRequest.current.abort();
    },
    []
  );

  // `form.reset()` puts every native input back to the value it was rendered with, and knows
  // nothing about a React component holding its own state - so a control that is not reset stays
  // showing, and posting, whatever was last picked. The member search form's "Clear" link is
  // exactly this: it calls `reset()` on the form and then tells each widget to redraw.
  //
  // Going back to `props.value` rather than to nothing is what makes this the same behaviour: a
  // native input resets to the value in its markup, which for these is what the server rendered,
  // not blank.
  useEffect(() => {
    const form = container.current && container.current.closest("form");
    if (!form) return undefined;

    const handleReset = () => setValues(resetValue.current);
    form.addEventListener("reset", handleReset);

    return () => form.removeEventListener("reset", handleReset);
  }, []);

  // react-select 5 hands `loadOptions` the term and expects a promise of the options array. The
  // previous implementation called `callback(error, { options })`, which is the react-select 1
  // signature - it was never reached, because nothing passed `ajax` until now.
  const loadOptions = useCallback(
    (term) => {
      const query = normalizeSearchTerm(term);
      clearTimeout(pendingKeystroke.current);
      pendingKeystroke.current = null;
      if (pendingResolve.current) {
        pendingResolve.current([]);
        pendingResolve.current = null;
      }
      if (pendingRequest.current) pendingRequest.current.abort();
      pendingRequest.current = null;
      // Before the length check, not after: a term that is too short asks nothing of the server, so
      // there is no failure left to report. Clearing it only on success would leave the last
      // failure on screen while the next search is in flight, and after the field is emptied.
      setSearchFailed(false);

      if (query.length < MIN_TERM_LENGTH) return Promise.resolve([]);

      return new Promise((resolve) => {
        pendingResolve.current = resolve;
        pendingKeystroke.current = setTimeout(() => {
          pendingKeystroke.current = null;
          const request = new AbortController();
          pendingRequest.current = request;

          getJSON(props.url, { q: query }, { signal: request.signal })
            .then((options) => {
              setSearchFailed(false);
              if (pendingRequest.current === request) pendingRequest.current = null;
              if (pendingResolve.current === resolve) pendingResolve.current = null;
              resolve(options || []);
            })
            .catch((error) => {
              // A superseded request is not a failure, and its promise is one react-select has
              // already stopped waiting on.
              if (error.name === "AbortError") {
                if (pendingRequest.current === request) pendingRequest.current = null;
                if (pendingResolve.current === resolve) pendingResolve.current = null;
                resolve([]);
                return;
              }

              setSearchFailed(true);
              if (pendingRequest.current === request) pendingRequest.current = null;
              if (pendingResolve.current === resolve) pendingResolve.current = null;
              resolve([]);
            });
        }, TYPE_DELAY);
      });
    },
    [props.url]
  );

  // react-select shows one message for "nothing matched", so it has to say which of the three
  // states this is. ajax-chosen distinguished them too, but only ever in English: it was never
  // handed `keepTypingMsg`, so it fell back to its own hardcoded default even in Danish.
  const noOptionsMessage = useCallback(
    ({ inputValue }) => {
      const query = normalizeSearchTerm(inputValue);
      // Length first: a term too short to search cannot have failed, whatever the flag still says.
      // Emptying the field does not call loadOptions at all, so resetting the flag when a load
      // starts does not cover that case - deciding by what is in the box does.
      if (query.length < MIN_TERM_LENGTH) return i18n.t("select.keep_typing");
      if (searchFailed) return i18n.t("select.search_failed");

      return i18n.t("select.no_results_text");
    },
    [searchFailed]
  );

  const loadingMessage = useCallback(({ inputValue }) => {
    const query = normalizeSearchTerm(inputValue);

    return `${i18n.t("select.looking_for_message")} ${query}...`;
  }, []);

  const handleChange = useCallback((value) => {
    setValues(value);
  }, []);

  const renderInputs = () => {
    // A multi select posts `name[]`, which Rails reads back as an array. A single one posts the
    // bare name: `guest_id[]` would arrive as `["5"]`, and the access-pass preview does
    // `params[:guest_id].to_i`.
    const name = isMulti ? `${props.name}[]` : props.name;

    // It needs to include an empty, to be able to clear it.
    // Otherwise it will not be included in params, so it won't be changed.
    //
    // For a single select that leaves two inputs under one name while something is selected. That
    // is deliberate and order-dependent: the empty one is rendered first, and Rack's
    // `parse_nested_query` keeps the last value it sees for a scalar key, as does jQuery's
    // `serializeArray` once the preview modal posts it. So the empty input only decides the value
    // when it is the only one left - which is exactly what clearing the field produces.
    let valuesArray = [];
    if (Array.isArray(values)) {
      valuesArray = values;
    } else if (values) {
      valuesArray = [values];
    }

    // A disabled `<select>` posts nothing at all, and Rails knows it: `select_content_tag` renders
    // the companion hidden field as `<input type="hidden" disabled>` when the select is disabled,
    // so that a locked field arrives as absent rather than as cleared. These have to do the same,
    // or opening and saving a form would write back a value its owner is not allowed to edit.
    return (
      <div>
        <input type="hidden" name={name} value="" readOnly disabled={isDisabled} />
        {valuesArray.map((value, _) => (
          <input
            type="hidden"
            key={value.value}
            name={name}
            value={value.value}
            readOnly
            disabled={isDisabled}
          />
        ))}
      </div>
    );
  };

  const reactSelectProps = Object.assign({}, props);
  // Not overridable from the call site: props arrive as JSON from ReactSelectInput, and
  // react-select's `styles` entries are functions.
  reactSelectProps.styles = selectStyles;
  const { ajax } = reactSelectProps;

  // Map "text" property to "label" for react-select
  reactSelectProps.getOptionLabel = (option) => option.text || option.label;
  // Stringified because the two sources disagree: ReactSelectInput renders ids through `to_s`,
  // while the search endpoints serialise them as JSON numbers. react-select compares these
  // strictly, so `"5"` and `5` are two different members - it offers one that is already chosen,
  // and picking it submits the same id twice.
  reactSelectProps.getOptionValue = (option) => String(option.value);

  if (ajax !== undefined) {
    // None of these are react-select props; they configure the loader above.
    delete reactSelectProps.ajax;
    delete reactSelectProps.url;

    reactSelectProps.loadOptions = loadOptions;
    reactSelectProps.loadingMessage = loadingMessage;
    reactSelectProps.noOptionsMessage = noOptionsMessage;
    // What the server already knows about: for a multi select these are the current selections, so
    // the menu is not empty before the first search. `options` is ignored by the async component.
    reactSelectProps.defaultOptions = reactSelectProps.options || [];
  }

  // Both shapes are driven the same way. Only the multi one used to be, which left a single select
  // holding a `value` it had no `onChange` to replace - controlled, and unable to change. `name` is
  // withheld from react-select, which would otherwise render a hidden input of its own beside the
  // ones above, under the same name.
  delete reactSelectProps.name;
  delete reactSelectProps.multi; // ours, not react-select's - it reads `isMulti`
  reactSelectProps.value = values;
  reactSelectProps.onChange = handleChange;
  reactSelectProps.isMulti = isMulti;
  const component = reactSelectProps.loadOptions ? Async : ReactSelect;
  const reactSelect = createElement(component, reactSelectProps);

  return (
    <div ref={container}>
      {renderInputs()}
      {reactSelect}
    </div>
  );
};

export default Select;
